import React, { useState, useEffect, Fragment, ChangeEvent, useRef, FormEvent, Dispatch, SetStateAction, CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';

import { Send, MoveLeft, User, MoveRight, MessageCircle } from 'lucide-react';

import { useGetChats, useCreateChat } from '../../hooks/chats';
import { useGetSimulationStageDetails, useGetSimulationWithStages } from '../../hooks/simulations';
import { useCompleteConversation } from '../../hooks/conversations';

import { EChatSource, ESimulationKey, ESimulationStageKey, ESimulationStageStatus } from '../../types/entities.enum';
import { IChatMessage } from '../../types/misc.interface';

import { getInteractionLimits, getSimulationColor } from '../../helpers/misc.helper';

export const Chat = () => {
    const lastMessageRef = useRef<HTMLDivElement>(null);
    const userTypingRef = useRef<HTMLDivElement>(null);
    const botTypingRef = useRef<HTMLDivElement>(null);

    const { simulationKey, stageKey, conversationId } = useParams();

    const [message, setMessage] = useState<string>('');
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const [isSending, setIsSending] = useState<boolean>(false);
    const [isGeneratingResponse, setIsGeneratingResponse] = useState<boolean>(false);
    const [chats, setChats] = useState<IChatMessage[]>([]);
    const [isCompletionWindowVisible, setIsCompletionWindowVisible] = useState<boolean>(false);
    const [messageThreshold, setMessageThreshold] = useState<'level1' | 'level2' | null>(null);
    const [continueMode, setContinueMode] = useState<boolean>(false);
    const [isProcessingOverview, setIsProcessingOverview] = useState<boolean>(false);
    const [nextOperation, setNextOperation] = useState<'next-persona' | 'analysis' | null>(null);
    const [nextStageDetails, setNextStageDetails] = useState<{ stageKey: ESimulationStageKey, action: 'start' | 'continue' } | null>(null);
    const [isIntermediateProcessWindowVisible, setIsIntermediateProcessWindowVisible] = useState<boolean>(false);

    const { data: stageDetails, isLoading: isLoadingStageDetails } = useGetSimulationStageDetails(simulationKey ? simulationKey as ESimulationKey : null, stageKey ? stageKey as ESimulationStageKey : null);
    const { data: simulationDetails } = useGetSimulationWithStages(simulationKey ? simulationKey as ESimulationKey : null);
    const { data: fetchedChats, isLoading: isLoadingChats } = useGetChats(conversationId ?? null);
    const { mutate: createChat } = useCreateChat();
    const { mutate: completeConversation } = useCompleteConversation();

    useEffect(() => {
        if (fetchedChats) {
            setChats(fetchedChats.map((chat) => ({
                by: chat.source,
                message: chat.message,
                timestamp: chat.created_at,
            })));

            const botMessages = fetchedChats.filter((chat) => chat.source === EChatSource.BOT);
            const limits = getInteractionLimits(simulationKey ? simulationKey as ESimulationKey : null);

            if (botMessages.length >= limits.level2) {
                setMessageThreshold('level2');

                setTimeout(() => {
                    setIsCompletionWindowVisible(true);
                }, 5000);
            } else if (!continueMode && botMessages.length >= limits.level1) {
                setMessageThreshold('level1');

                setTimeout(() => {
                    setIsCompletionWindowVisible(true);
                }, 5000);
            };
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchedChats]);

    const setBottomScroll = () => {
        if (lastMessageRef.current) {
            lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        };

        if (userTypingRef.current) {
            userTypingRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        };

        if (botTypingRef.current) {
            botTypingRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        };
    };

    const onChatMessageChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (isSending || isGeneratingResponse) return;

        // setBottomScroll();
        setIsTyping(true);
        setMessage(e.target.value);
    };

    const onChatMessageBlur = () => {
        setTimeout(() => {
            setIsTyping(false);
        }, 500);
    };

    const onChatMessageSend = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!message?.trim()?.length || !conversationId || isSending || isGeneratingResponse || messageThreshold === 'level2') return;

        createChat({
            payload: {
                conversation_id: conversationId,
                source: EChatSource.USER,
                message,
            },
            simulationKey: simulationKey ? simulationKey as ESimulationKey : null,
            setIsTyping,
            setIsSending,
            chats,
            setChats,
            setMessage,
            setIsGeneratingResponse,
            setBottomScroll,
            setIsCompletionWindowVisible,
            messageThreshold,
            setMessageThreshold,
            continueMode,
        });
    };

    useEffect(() => {
        if (!stageDetails || !simulationDetails) return;

        if (simulationDetails.stages.length > 1) {
            const idx = simulationDetails.stages.findIndex(item => item.key === stageDetails.key);
            if (idx === -1) return;

            if (idx < simulationDetails.stages.length - 1) {
                setNextOperation('next-persona');
                const nxtStage = simulationDetails.stages[idx + 1];
                if (nxtStage.status !== ESimulationStageStatus.COMPLETED) {
                    setNextStageDetails({
                        stageKey: nxtStage.key,
                        action: nxtStage.status === ESimulationStageStatus.NOT_STARTED ? 'start' : 'continue',
                    });
                };
            } else {
                setNextOperation('analysis');
            };
        } else {
            setNextOperation('analysis');
        };

    }, [stageDetails, simulationDetails]);

    const isHandleNextDisabled = (): boolean => {
        if (!simulationKey || !conversationId || !nextOperation) return true;

        const botMessages = chats.filter((chat) => chat.by === EChatSource.BOT);
        const limits = getInteractionLimits(simulationKey ? simulationKey as ESimulationKey : null);
        if (botMessages.length < limits.eligibility) return true;

        return false;
    };

    const handleNext = () => {
        if (isHandleNextDisabled() || !conversationId || !nextOperation) return;

        const botMessages = chats.filter((chat) => chat.by === EChatSource.BOT);
        const limits = getInteractionLimits(simulationKey ? simulationKey as ESimulationKey : null);

        if (botMessages.length >= limits.eligibility && botMessages.length < limits.level1) {
            setIsIntermediateProcessWindowVisible(true);
        } else {
            completeConversation({
                conversationId,
                simulationKey: simulationKey as ESimulationKey,
                setIsProcessing: setIsProcessingOverview,
                nextOperation,
                nextStageDetails,
            });
        };
    };

    return (
        <div
            className='chat__page'
            style={{ '--simulation-theme-color': getSimulationColor(simulationKey ? simulationKey as ESimulationKey : null) } as CSSProperties}
        >
            {isLoadingStageDetails && !stageDetails && (
                <div className='d-flex flex-column align-items-center justify-content-center gap-sm'>
                    <div className='list-loader' />
                    <p className='text-medium color--gray-700'>Loading stage details</p>
                </div>
            )}
            {!isLoadingStageDetails && !stageDetails && (
                <p className='text-lg text-semibold color--gray-600 text-center'>No stages details found!</p>
            )}
            {!isLoadingStageDetails && !!stageDetails && (
                <Fragment>
                    <CompletionWindow
                        visible={isCompletionWindowVisible}
                        setVisible={setIsCompletionWindowVisible}
                        setContinueMode={setContinueMode}
                        simulationKey={simulationKey ? simulationKey as ESimulationKey : null}
                        conversationId={conversationId ?? ''}
                        messageThreshold={messageThreshold}
                        setMessageThreshold={setMessageThreshold}
                        nextOperation={nextOperation}
                        nextStageDetails={nextStageDetails}
                    />

                    <IntermediateProcessWindow
                        visible={isIntermediateProcessWindowVisible}
                        setVisible={setIsIntermediateProcessWindowVisible}
                        recommMsgs={getInteractionLimits(simulationKey ? simulationKey as ESimulationKey : null).level1}
                        simulationKey={simulationKey ? simulationKey as ESimulationKey : null}
                        conversationId={conversationId ?? ''}
                        nextOperation={nextOperation}
                        nextStageDetails={nextStageDetails}
                    />

                    <div className='card card--md chat__view'>
                        <div className='chat__view__top'>
                            <div className='chat__view__top__icon'>
                                <MessageCircle />
                            </div>
                            <div>
                                <p className='text-xl text-bold'>{stageDetails.person}</p>
                                <p className='color--gray-600 mb-xs'>{stageDetails.title}</p>
                                <p className='text-sm color--gray-500'>{stageDetails.description}</p>
                            </div>
                        </div>
                        <div className='chat__view__body'>
                            {isLoadingChats && !chats && (
                                <div className='d-flex flex-column align-items-center justify-content-center gap-sm'>
                                    <div className='list-loader' />
                                    <p className='text-medium color--gray-700'>Loading chats</p>
                                </div>
                            )}
                            {!isLoadingChats && !chats && (
                                <p className='text-lg text-semibold color--gray-600 text-center'>No chats available!</p>
                            )}
                            {!isLoadingChats && !!chats?.length && (
                                <div className='chat__messages'>
                                    {chats.map((chat, index) => (
                                        <ChatMessage
                                            key={index}
                                            {...chat}
                                            custRef={index === chats.length - 1 ? lastMessageRef : undefined}
                                            persona={{ name: stageDetails.person, role: stageDetails.title }}
                                        />
                                    ))}
                                    {isGeneratingResponse && (
                                        <ChatMessage
                                            by={EChatSource.BOT}
                                            isTyping={true}
                                            custRef={botTypingRef}
                                            persona={{ name: stageDetails.person, role: stageDetails.title }}
                                        />
                                    )}
                                    {isTyping && (
                                        <ChatMessage
                                            by={EChatSource.USER}
                                            isTyping={true}
                                            custRef={userTypingRef}
                                            persona={{ name: stageDetails.person, role: stageDetails.title }}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                        <div className='chat__view__bottom'>
                            <form onSubmit={onChatMessageSend}>
                                <input
                                    type='text'
                                    placeholder='Type your message here...'
                                    value={message}
                                    onChange={onChatMessageChange}
                                    onBlur={onChatMessageBlur}
                                />
                                <button
                                    type='submit'
                                    disabled={!message?.trim()?.length || messageThreshold === 'level2'}
                                    className='btn btn--lg btn--primary'
                                >
                                    {isSending ? <span className='btn-loader' /> : <Send />}
                                    {isSending ? <span>Sending</span> : <span>Send</span>}
                                </button>
                            </form>
                        </div>
                    </div>
                </Fragment>
            )}
            <div className='chat__actions'>
                <Link
                    to={`/simulation/${simulationKey}/stages`}
                    className='link link--md link--primary'
                >
                    <MoveLeft />
                    <span>Back to Overview</span>
                </Link>
                <button
                    className='btn btn--lg btn--primary'
                    disabled={isHandleNextDisabled()}
                    onClick={handleNext}
                >
                    {isProcessingOverview
                        ? <span className='btn-loader' />
                        : <Fragment>
                            {nextOperation === 'analysis' ? <span>View&nbsp;Analysis</span> : !!nextStageDetails ? <span>Next&nbsp;Persona</span> : <span>Go&nbsp;to&nbsp;Overview</span>}
                        </Fragment>
                    }
                    <MoveRight />
                </button>
            </div>
        </div>
    );
};

const ChatMessage = (props: IChatMessage) => {
    const { by, message, timestamp, isTyping, typingEffect, custRef, persona } = props;

    const [messageValue, setMessageValue] = useState<string>('');

    useEffect(() => {
        if (custRef?.current) {
            custRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'center' });
        };
    }, [custRef]);

    useEffect(() => {
        if (message) {
            let interval: NodeJS.Timeout | null = null;

            if (!typingEffect) {
                setMessageValue(message);
            } else {
                interval = setInterval(() => {
                    setMessageValue((prev) => {
                        if (message.length === prev.length + 1) {
                            if (interval) clearInterval(interval);
                            return message;
                        }
                        return message.slice(0, prev.length + 1);
                    });
                }, 40);
            }
            return () => {
                if (interval) clearInterval(interval);
            };
        };

        // eslint-disable-next-line
    }, [message]);

    return (
        <div
            ref={custRef}
            className={`chat__chat_message chat__chat_message--from-${by}`}
        >
            <div className='chat__chat_message__left'>
                <div className='chat__chat_message__icon'>
                    {/* {by === 'bot' ? <Bot /> : <User />} */}
                    <User />
                </div>
                {timestamp && <p className='text-xs color--gray-500'>{moment(timestamp).format('HH:mm')}</p>}
            </div>
            <div className='chat__chat_message__right'>
                <div className='chat__chat_message__user_info'>
                    {by === 'bot'
                        ? <p className='text-xs'><span className='text-medium'>{persona?.name}</span> <span className='color--gray-500'>{persona?.role}</span></p>
                        : <p className='text-xs text-medium'>You</p>
                    }
                </div>
                <div className='chat__chat_message__message_body'>
                    {isTyping && !messageValue && (
                        <div className='chat__chat_message__message_body__typing_dots'>
                            <div className='chat__chat_message__message_body__typing_dots__dot' />
                            <div className='chat__chat_message__message_body__typing_dots__dot' />
                            <div className='chat__chat_message__message_body__typing_dots__dot' />
                        </div>
                    )}
                    {!isTyping && messageValue && (
                        <p className='text-sm chat__chat_message__message_body__text'>{messageValue}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

interface ICompletionWindowProps {
    visible: boolean;
    setVisible: Dispatch<SetStateAction<boolean>>;
    setContinueMode: Dispatch<SetStateAction<boolean>>;
    conversationId: string;
    simulationKey: ESimulationKey | null;
    messageThreshold: 'level1' | 'level2' | null;
    setMessageThreshold: Dispatch<SetStateAction<'level1' | 'level2' | null>>;
    nextOperation: 'next-persona' | 'analysis' | null;
    nextStageDetails: { stageKey: ESimulationStageKey; action: 'start' | 'continue' } | null;
};
const CompletionWindow = (props: ICompletionWindowProps) => {
    const { visible, setVisible, setContinueMode, conversationId, simulationKey, messageThreshold, setMessageThreshold, nextOperation, nextStageDetails } = props;

    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const { mutate: completeConversation } = useCompleteConversation();

    const handleNext = () => {
        if (!simulationKey || !nextOperation) return;

        completeConversation({
            conversationId,
            simulationKey,
            setIsProcessing,
            nextOperation,
            nextStageDetails,
        });
    };

    const handleContinueConversation = () => {
        setContinueMode(true);
        setVisible(false);
        setMessageThreshold('level1');
    };

    return (
        <div className={`chat__conversation_completion_window ${visible ? 'chat__conversation_completion_window--visible' : ''}`}>
            <div className='card card--md chat__conversation_completion_window__content'>
                <p className='text-2xl text-bold color--primary text-center mb-lg'>Your conversation analysis is ready</p>
                <p className='color--gray-600 text-center'>
                    {
                        messageThreshold !== 'level2'
                            ? 'You can choose to end the conversation or continue till you achieve your goal.'
                            : 'Thank you for completing the conversation. You can now proceed with the next persona.'
                    }
                </p>
                <div className='chat__conversation_completion_window__actions'>
                    <button className='btn btn--fluid btn--lg btn--primary' onClick={handleNext}>
                        {isProcessing
                            ? <span className='btn-loader' />
                            : <Fragment>
                                {nextOperation === 'analysis' ? <span>Go&nbsp;to&nbsp;Analysis</span> : !!nextStageDetails ? <span>Next&nbsp;Persona</span> : <span>Go&nbsp;to&nbsp;Overview</span>}
                            </Fragment>
                        }
                    </button>
                    {messageThreshold !== 'level2' &&
                        <button className='btn btn--fluid btn--lg btn--secondary' onClick={handleContinueConversation}>
                            Stay&nbsp;in&nbsp;Conversation
                        </button>
                    }
                </div>
            </div>
        </div>
    );
};

interface IIntermediateProcessWindowProps {
    visible: boolean;
    setVisible: Dispatch<SetStateAction<boolean>>;
    recommMsgs: number;
    conversationId: string;
    simulationKey: ESimulationKey | null;
    nextOperation: 'next-persona' | 'analysis' | null;
    nextStageDetails: { stageKey: ESimulationStageKey; action: 'start' | 'continue' } | null;
};
const IntermediateProcessWindow = (props: IIntermediateProcessWindowProps) => {
    const { visible, setVisible, recommMsgs, conversationId, simulationKey, nextOperation, nextStageDetails } = props;

    const [txt, setTxt] = useState('');
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const { mutate: completeConversation } = useCompleteConversation();

    const handleNext = () => {
        if (!simulationKey || !nextOperation || txt !== "Proceed") return;

        completeConversation({
            conversationId,
            simulationKey,
            setIsProcessing,
            nextOperation,
            nextStageDetails,
        });
    };

    return (
        <div className={`chat__conversation_intermediate_process_window ${visible ? 'chat__conversation_intermediate_process_window--visible' : ''}`}>
            <div className='card card--md chat__conversation_intermediate_process_window__content'>
                <p className='text-2xl text-bold color--primary text-center mb-md'>
                    Do you want to continue?
                </p>
                <p className='color--gray-600 text-center mb-2xl'>
                    We recommend completing at least {recommMsgs} interactions before proceeding.
                </p>
                <p className='text-sm mb-xs'>
                    Please type <strong>Proceed</strong> to enable the button.
                </p>
                <input
                    type="text"
                    placeholder='Type "Proceed" to continue'
                    value={txt}
                    onChange={e => setTxt(e.target.value)}
                />
                <div className='chat__conversation_intermediate_process_window__actions'>
                    <button
                        className='btn btn--fluid btn--lg btn--primary'
                        onClick={handleNext}
                        disabled={txt !== "Proceed"}
                    >
                        {isProcessing
                            ? <span className='btn-loader' />
                            : <Fragment>
                                {nextOperation === 'analysis' ? <span>Go&nbsp;to&nbsp;Analysis</span> : !!nextStageDetails ? <span>Next&nbsp;Persona</span> : <span>Go&nbsp;to&nbsp;Overview</span>}
                            </Fragment>
                        }
                    </button>
                    <button
                        className='btn btn--fluid btn--lg btn--secondary'
                        onClick={() => setVisible(false)}
                    >
                        Discard
                    </button>
                </div>
            </div>
        </div>
    );
};